import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { memo, useMemo } from 'react';

import useMatch from '../../MatchUnit/useMatch';
import { getMatchesQuery } from '../../queries/matches';
import { getHeadToHeadQuery } from '../../queries/statistics';

import HeadToHeadContent from './HeadToHeadContent';
import { getStat } from './utils';

import { getHeadToHeadUrl } from '@services/LinkRuleService';
import type { Match } from '@shared-types/match.types';

import * as classes from './styles.module.scss';

const HeadToHead = ({
  options,
  options: { showMatchH2HCta = false, extendedView = false, competitionIds = '' },
}: {
  options: { match: Match; showMatchH2HCta?: boolean; extendedView?: boolean; competitionIds?: string };
}) => {
  const match = useMatch(options.match);
  const { data: historyMatches } = useQuery(
    getMatchesQuery({
      competitionId: competitionIds ? competitionIds : match.competition.id,
      opponentTeamIds: [match.homeTeam.id, match.awayTeam.id],
      seasonsRange: competitionIds ? null : 'HISTORICAL',
      limit: 1,
      offset: 0,
    }),
  );
  const { data: headToHead } = useQuery(
    getHeadToHeadQuery({
      team1Id: match.homeTeam.id,
      team2Id: match.awayTeam.id,
      competitionId: competitionIds ? competitionIds : match.competition.id,
    }),
  );

  const h2hLink = useMemo(
    () => (historyMatches?.length ? getHeadToHeadUrl(match.homeTeam.id, match.awayTeam.id, match.competition?.id) : ''),
    [historyMatches?.length, match.awayTeam.id, match.competition?.id, match.homeTeam.id],
  );
  const home = useMemo(
    () => headToHead?.find((t) => t.teamId === `${match.homeTeam.id}`),
    [headToHead, match.homeTeam.id],
  );
  const away = useMemo(
    () => headToHead?.find((t) => t.teamId === `${match.awayTeam.id}`),
    [headToHead, match.awayTeam.id],
  );

  const appearance = getStat(home, 'matches_appearance');

  if (!+appearance && !extendedView) return null;

  return (
    <div
      className={clsx({
        [classes.container]: true,
        [classes.extended]: extendedView,
        'card-match': !extendedView,
      })}
    >
      <HeadToHeadContent
        options={options}
        showMatchH2HCta={showMatchH2HCta}
        home={home}
        away={away}
        h2hLink={h2hLink}
        extendedView={extendedView}
      />
    </div>
  );
};

export default memo(HeadToHead);
